import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Mail, Send, Users, Filter, Plus, Calendar, 
  CheckCircle, Clock, AlertCircle, X, User,
  Zap, FileText, Target
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEGMENTS = [
  { id: 'all', label: 'Todos los usuarios', icon: Users, desc: 'Enviar a todos' },
  { id: 'low_credits', label: 'Créditos bajos', icon: Zap, desc: 'Usuarios con menos de 20 créditos' },
  { id: 'inactive', label: 'Inactivos', icon: Clock, desc: 'Sin login en 30+ días' },
  { id: 'by_profession', label: 'Por profesión', icon: Target, desc: 'Filtrar por oposición' },
  { id: 'by_plan', label: 'Por plan', icon: FileText, desc: 'Filtrar por suscripción' }
];

const EMAIL_TEMPLATES = [
  {
    id: 'low_credits',
    name: 'Créditos bajos',
    subject: '⚡ ¡Tus créditos IA se están agotando!',
    content: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #7C3AED, #4F46E5); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
    <h1 style="color: white; margin: 0;">⚡ Recarga tus créditos</h1>
  </div>
  <div style="background: #f9fafb; padding: 30px;">
    <p>Hola {{name}},</p>
    <p>Hemos notado que tus créditos IA están bajando. ¡No dejes que tu preparación se detenga!</p>
    <p>Recarga ahora y obtén un <strong>20% de descuento</strong> con el código:</p>
    {{offer_section}}
    <a href="https://tuplazaia.com/dashboard" style="display: inline-block; background: #4F46E5; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; margin-top: 20px;">
      Recargar Créditos
    </a>
  </div>
</div>`
  },
  {
    id: 'inactive',
    name: 'Reactivación',
    subject: '👋 ¡Te echamos de menos en TuPlazaIA!',
    content: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #059669, #10B981); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
    <h1 style="color: white; margin: 0;">👋 ¡Vuelve a estudiar!</h1>
  </div>
  <div style="background: #f9fafb; padding: 30px;">
    <p>Hola {{name}},</p>
    <p>Hace tiempo que no te vemos por aquí. Tu plaza te está esperando.</p>
    <p>Vuelve hoy y recibe <strong>50 créditos gratis</strong> para tu Tutor IA.</p>
    <a href="https://tuplazaia.com" style="display: inline-block; background: #10B981; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; margin-top: 20px;">
      Retomar mi preparación
    </a>
  </div>
</div>`
  },
  {
    id: 'new_features',
    name: 'Nuevas funciones',
    subject: '🚀 Nuevas funciones en TuPlazaIA',
    content: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #4F46E5, #7C3AED); padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
    <h1 style="color: white; margin: 0;">🚀 Novedades</h1>
  </div>
  <div style="background: #f9fafb; padding: 30px;">
    <p>Hola {{name}},</p>
    <p>¡Tenemos novedades emocionantes para ti!</p>
    <ul style="line-height: 1.8;">
      <li>✨ Nuevo sistema de créditos IA</li>
      <li>🎯 CRM integrado para academias</li>
      <li>📊 Estadísticas mejoradas</li>
    </ul>
    <a href="https://tuplazaia.com/dashboard" style="display: inline-block; background: #4F46E5; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; margin-top: 20px;">
      Descubrir novedades
    </a>
  </div>
</div>`
  }
];

const EmailCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    html_content: '',
    segment: 'all',
    segment_filters: {}
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/leads/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaigns(res.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setFormData({
      ...formData,
      name: template.name,
      subject: template.subject,
      html_content: template.content,
      segment: template.id === 'low_credits' ? 'low_credits' : 
               template.id === 'inactive' ? 'inactive' : 'all'
    });
  };

  const handleCreateCampaign = async () => {
    if (!formData.name || !formData.subject || !formData.html_content) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.post(`${API}/leads/campaigns/create`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Campaña creada: ${res.data.recipient_count} destinatarios`);
      setShowCreate(false);
      fetchCampaigns();
    } catch (error) {
      toast.error('Error al crear la campaña');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('¿Enviar esta campaña ahora?')) return;
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/leads/campaigns/${campaignId}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Campaña enviándose...');
      fetchCampaigns();
    } catch (error) {
      toast.error('Error al enviar la campaña');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Pendiente' },
      scheduled: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Calendar, label: 'Programada' },
      sending: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Send, label: 'Enviando' },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Completada' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Campañas de Email</h2>
          <p className="text-slate-600">Envía ofertas y comunicaciones segmentadas</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Nueva Campaña
        </Button>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Campaña</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Segmento</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Destinatarios</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Estado</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Resultados</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                  Cargando campañas...
                </td>
              </tr>
            ) : campaigns.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay campañas aún</p>
                  <p className="text-sm">Crea tu primera campaña para empezar</p>
                </td>
              </tr>
            ) : (
              campaigns.map((campaign) => (
                <tr key={campaign.campaign_id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-800">{campaign.name}</div>
                    <div className="text-sm text-slate-500">{campaign.subject}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="capitalize">{campaign.segment.replace('_', ' ')}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-4 h-4 text-slate-400" />
                      {campaign.recipient_count}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(campaign.status)}
                  </td>
                  <td className="px-6 py-4">
                    {campaign.status === 'completed' ? (
                      <div className="text-sm">
                        <span className="text-green-600">{campaign.sent_count || 0} enviados</span>
                        {campaign.failed_count > 0 && (
                          <span className="text-red-600 ml-2">{campaign.failed_count} fallidos</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {campaign.status === 'pending' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleSendCampaign(campaign.campaign_id)}
                        className="gap-1"
                      >
                        <Send className="w-3 h-3" />
                        Enviar
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Campaign Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-slate-800">Nueva Campaña</h3>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Templates */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Plantillas rápidas
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {EMAIL_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedTemplate?.id === template.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="font-medium text-slate-800">{template.name}</div>
                      <div className="text-xs text-slate-500 mt-1 truncate">{template.subject}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Nombre de la campaña *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ej: Reactivación Q1 2025"
                  />
                </div>
                
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Asunto del email *
                  </label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ej: ⚡ ¡Tus créditos se agotan!"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Segmento de usuarios
                  </label>
                  <select
                    value={formData.segment}
                    onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    {SEGMENTS.map((seg) => (
                      <option key={seg.id} value={seg.id}>{seg.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {SEGMENTS.find(s => s.id === formData.segment)?.desc}
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Contenido HTML *
                  </label>
                  <textarea
                    value={formData.html_content}
                    onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
                    rows={8}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                    placeholder="<div>Tu contenido HTML aquí...</div>"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Variables disponibles: {"{{name}}"}, {"{{email}}"}, {"{{offer_section}}"}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCreate(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateCampaign} className="gap-2">
                <Mail className="w-4 h-4" />
                Crear Campaña
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaigns;
